import { useCallback, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import type { GroupEntity } from '@group/domain';
import * as EmailValidator from 'email-validator';
import type { License } from './LicenseDescriptionPanel';

const GOOGLE_FORM_INFO = {
  ACTION_URL:
    'https://docs.google.com/forms/u/0/d/e/1FAIpQLSezaZ0uh-lBwChXJddVe7ffkBPAuW6hL3TYkuagEJZ4nHOaxA/formResponse',
  GROUP_ID_ID: '982490265',
  LICENSE_KIND_ID: '1753432711',
  NUMBER_OF_LICENSES_ID: '1989307601',
  LICENSE_START_ID: '1652243403',
  LICENSE_END_ID: '1477180458',
  LICENSE_FEE_ID: '1356580191',
  NAME_ID: '422843853',
  COMPANY_ID: '2048009480',
  MAIL_ADDRESS_ID: '1673831543',
  TEL_NO_ID: '1958458518',
  INVOICE_ADDRESS_ID: '38247444',
  OTHER_REQUESTS_ID: '1966962872',
};

const FIELD_INFO: { [key: string]: { name: string; label: string } } = {
  groupId: {
    name: `entry.${GOOGLE_FORM_INFO.GROUP_ID_ID}`,
    label: 'グループID',
  },
  licenseKind: {
    name: `entry.${GOOGLE_FORM_INFO.LICENSE_KIND_ID}`,
    label: 'ライセンス種類',
  },
  numberOfLicenses: {
    name: `entry.${GOOGLE_FORM_INFO.NUMBER_OF_LICENSES_ID}`,
    label: 'ライセンス数',
  },
  licenseStart: {
    name: `entry.${GOOGLE_FORM_INFO.LICENSE_START_ID}`,
    label: 'ライセンス開始月',
  },
  licenseEnd: {
    name: `entry.${GOOGLE_FORM_INFO.LICENSE_END_ID}`,
    label: 'ライセンス終了月',
  },
  licenseFee: {
    name: `entry.${GOOGLE_FORM_INFO.LICENSE_FEE_ID}`,
    label: 'ライセンス料（自動計算）',
  },
  name: {
    name: `entry.${GOOGLE_FORM_INFO.NAME_ID}`,
    label: 'お名前',
  },
  company: {
    name: `entry.${GOOGLE_FORM_INFO.COMPANY_ID}`,
    label: 'ご所属（会社名など）',
  },
  mailAddress: {
    name: `entry.${GOOGLE_FORM_INFO.MAIL_ADDRESS_ID}`,
    label: 'メールアドレス',
  },
  telNo: {
    name: `entry.${GOOGLE_FORM_INFO.TEL_NO_ID}`,
    label: '電話番号',
  },
  invoiceAddress: {
    name: `entry.${GOOGLE_FORM_INFO.INVOICE_ADDRESS_ID}`,
    label: '請求書宛名',
  },
};

type Props = {
  onClose(): void;
  onApplyCompleted(): void;
  group: GroupEntity;
  license: License;
};

export const ApplyLicenseForm: React.FC<Props> = ({ onClose, onApplyCompleted, group, license }: Props) => {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [mailAddressError, setMailAddressError] = useState<string>('');
  const [licensePeriodError, setLicensePeriodError] = useState<string>('');

  const [numberOfLicenses, setNumberOfLicenses] = useState(license.number.toString());
  const [licenseStart, setLicenseStart] = useState('');
  const [licenseEnd, setLicenseEnd] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [mailAddress, setMailAddress] = useState('');
  const [telNo, setTelNo] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [otherRequests, setOtherRequests] = useState('');

  const [licenseFee, setLicenseFee] = useState(0);
  const [targetMonths, setTargetMonths] = useState(0);

  const getMonthFromYm = (ym: string) => {
    const year = ym ? Number(ym.substring(0, 4)) : 0;
    const month = ym ? Number(ym.substring(5)) : 0;

    return year * 12 + month;
  };

  const calcLicenseFee = useCallback(
    (startYm: string, endYm: string, inputNumber: string) => {
      const startMonths = getMonthFromYm(startYm);
      const endMonths = getMonthFromYm(endYm);

      if (startMonths === 0 || endMonths === 0) {
        // いずれかが未入力の場合
        setLicenseFee(0);
        setTargetMonths(0);
        return;
      }

      const num = license.editable ? Number(inputNumber) : 1;
      const calculatedTargetMonths = endMonths - startMonths + 1;
      const calculatedFee = (license.monthlyFee * num * calculatedTargetMonths * 11) / 10;

      setLicenseFee(calculatedFee);
      setTargetMonths(calculatedTargetMonths);
    },
    [license.editable, license.monthlyFee]
  );

  const handleChangeNumberOfLicenses = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setNumberOfLicenses(event.target.value);
      calcLicenseFee(licenseStart, licenseEnd, inputValue);
    },
    [calcLicenseFee, licenseEnd, licenseStart]
  );

  const validateLicensePeriod = useCallback((startYm: string, endYm: string): string => {
    const startMonths = getMonthFromYm(startYm);
    const endMonths = getMonthFromYm(endYm);
    const now = new Date();
    const thisMonths = now.getFullYear() * 12 + now.getMonth() + 1;

    if (thisMonths > startMonths) {
      setLicenseStart('');
      setLicenseEnd(endYm);
      return `正しい${FIELD_INFO.licenseStart.label}を入力してください。`;
    }

    if (endMonths > 0 && startMonths > endMonths) {
      setLicenseStart(startYm);
      setLicenseEnd('');
      return `正しい${FIELD_INFO.licenseEnd.label}を入力してください。`;
    }

    return '';
  }, []);

  const handleChangeLicenseStart = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      const errorMessage = validateLicensePeriod(inputValue, licenseEnd);
      if (errorMessage) {
        setLicensePeriodError(errorMessage);
        return;
      }

      setLicensePeriodError('');
      setLicenseStart(inputValue);
      calcLicenseFee(inputValue, licenseEnd, numberOfLicenses);
    },
    [calcLicenseFee, licenseEnd, numberOfLicenses, validateLicensePeriod]
  );

  const handleChangeLicenseEnd = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      const errorMessage = validateLicensePeriod(licenseStart, inputValue);
      if (errorMessage) {
        setLicensePeriodError(errorMessage);
        return;
      }

      setLicensePeriodError('');
      setLicenseEnd(inputValue);
      calcLicenseFee(licenseStart, inputValue, numberOfLicenses);
    },
    [calcLicenseFee, licenseStart, numberOfLicenses, validateLicensePeriod]
  );

  const handleChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleChangeCompany = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCompany(event.target.value);
  }, []);

  const handleChangeMailAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMailAddress(event.target.value);
  }, []);

  const handleChangeTelNo = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTelNo(event.target.value);
  }, []);

  const handleChangeInvoiceAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceAddress(event.target.value);
  }, []);

  const handleChangeOtherRequests = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOtherRequests(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    setIsSubmitted(true);
  }, []);

  const initFields = () => {
    setLicenseStart('');
    setLicenseEnd('');
    setName('');
    setCompany('');
    setMailAddress('');
    setTelNo('');
    setInvoiceAddress('');
    setOtherRequests('');
  };

  const isInputCompleted = useCallback(() => {
    return !!licenseStart && !!licenseEnd && !!name && !!company && !!mailAddress && !!telNo && !!invoiceAddress;
  }, [company, invoiceAddress, licenseEnd, licenseStart, mailAddress, name, telNo]);

  const validateEmail = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!EmailValidator.validate(event.target.value)) {
      setMailAddressError(`正しい${FIELD_INFO.mailAddress.label}を入力してください。`);
      return;
    }

    setMailAddressError('');
  }, []);

  const onIframeLoaded = useCallback(() => {
    if (isSubmitted) {
      initFields();
      onApplyCompleted();
    }
  }, [isSubmitted, onApplyCompleted]);

  return (
    <form action={GOOGLE_FORM_INFO.ACTION_URL} method="POST" target="thanks_iframe" onSubmit={handleSubmit}>
      <iframe name="thanks_iframe" style={{ display: 'none' }} onLoad={onIframeLoaded} />

      <input type="hidden" name={FIELD_INFO.groupId.name} value={group.id} />

      <div className="mx-4" style={{ width: '920px', height: '660px' }}>
        <div className="flex flex-col px-8">
          <div className="flex flex-row">
            <div>
              <div className="w-96 pb-2 font-bold">{FIELD_INFO.licenseKind.label}</div>
              <div>{license.kind}</div>
              <input type="hidden" name={FIELD_INFO.licenseKind.name} value={license.kind} />
            </div>
            <div className="ml-8">
              <div className="pb-2 font-bold">{FIELD_INFO.licenseFee.label}</div>
              <div>{`${licenseFee.toLocaleString()}円（${targetMonths}ヶ月、税込）`}</div>
              <input type="hidden" name={FIELD_INFO.licenseFee.name} value={licenseFee} />
            </div>
          </div>

          <div className="flex flex-row">
            <div>
              <div className="pb-2 pt-8 font-bold">{FIELD_INFO.numberOfLicenses.label}</div>
              <div className="flex flex-row items-end">
                <div className="w-20 text-right text-xl">
                  <input
                    type="text"
                    name={FIELD_INFO.numberOfLicenses.name}
                    className="w-20 rounded-sm border p-2"
                    readOnly={!license.editable}
                    value={numberOfLicenses}
                    onChange={handleChangeNumberOfLicenses}
                    placeholder={`${FIELD_INFO.numberOfLicenses.label}を入力`}
                  />
                </div>
                <span className="ml-4">ライセンス</span>
              </div>
            </div>
            <div className="ml-60">
              <div className="pb-2 pt-8 font-bold">ライセンス期間（月初〜月末）</div>
              <div className="flex flex-row items-center">
                <div>
                  <input
                    type="month"
                    name={FIELD_INFO.licenseStart.name}
                    className="w-48 rounded-sm border p-2 text-lg"
                    value={licenseStart}
                    onChange={handleChangeLicenseStart}
                  />
                </div>
                <div className="mx-4 text-xl">〜</div>
                <div>
                  <input
                    type="month"
                    name={FIELD_INFO.licenseEnd.name}
                    className="w-48 rounded-sm border p-2 text-lg"
                    value={licenseEnd}
                    onChange={handleChangeLicenseEnd}
                  />
                </div>
              </div>
              <div>{licensePeriodError && <span className="text-xs text-red-700">{licensePeriodError}</span>}</div>
            </div>
          </div>

          <div className="pb-2 pt-8 font-bold">請求先情報</div>
          <div className="flex flex-row items-end">
            <div>
              <div className="pb-2">{FIELD_INFO.name.label}</div>
              <input
                type="text"
                name={FIELD_INFO.name.name}
                className="w-96 rounded-sm border p-2"
                value={name}
                onChange={handleChangeName}
                placeholder={`${FIELD_INFO.name.label}を入力`}
              />
            </div>
            <div className="ml-8">
              <div className="pb-2">{FIELD_INFO.company.label}</div>
              <input
                type="text"
                name={FIELD_INFO.company.name}
                className="w-[440px] rounded-sm border p-2"
                value={company}
                onChange={handleChangeCompany}
                placeholder={`${FIELD_INFO.company.label}を入力`}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-row items-end">
            <div>
              <div className="pb-2">
                {FIELD_INFO.mailAddress.label}
                {mailAddressError && <span className="ml-4 text-xs text-red-700">{mailAddressError}</span>}
              </div>
              <input
                type="text"
                id="mailAddress"
                name={FIELD_INFO.mailAddress.name}
                className="w-96 rounded-sm border p-2"
                value={mailAddress}
                onChange={handleChangeMailAddress}
                onBlur={validateEmail}
                placeholder={`${FIELD_INFO.mailAddress.label}を入力`}
              />
            </div>
            <div className="ml-8">
              <div className="pb-2">{FIELD_INFO.telNo.label}</div>
              <input
                type="text"
                name={FIELD_INFO.telNo.name}
                className="w-[440px] rounded-sm border p-2"
                value={telNo}
                onChange={handleChangeTelNo}
                placeholder={`${FIELD_INFO.telNo.label}を入力`}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-row items-start">
            <div>
              <div className="pb-2">{FIELD_INFO.invoiceAddress.label}</div>
              <input
                type="text"
                name={FIELD_INFO.invoiceAddress.name}
                className="w-96 rounded-sm border p-2"
                value={invoiceAddress}
                onChange={handleChangeInvoiceAddress}
                placeholder={`${FIELD_INFO.invoiceAddress.label}を入力`}
              />
            </div>
            <div className="ml-8">
              <div className="pb-2">ご要望等（オプション）</div>
              <textarea
                name={`entry.${GOOGLE_FORM_INFO.OTHER_REQUESTS_ID}`}
                value={otherRequests}
                onChange={handleChangeOtherRequests}
                className="w-[440px] rounded-sm border border-gray-500 p-2"
                rows={5}
                placeholder="メッセージを入力"
              />
            </div>
          </div>
        </div>
        <div className="ml-12 mt-6 text-sm">
          <ul>
            <li>※ 支払い方法は、請求書払い（銀行振込）となります。</li>
            <li>
              ※ 「申し込み」ボタンをクリック後、担当者からご入力いただいたメールアドレスに請求書を送付いたします。
            </li>
          </ul>
        </div>
      </div>
      <div className={'flex justify-end py-2'}>
        <div className={'pr-2'}>
          <Button
            type="submit"
            color="primary"
            disabled={!!mailAddressError || !!licensePeriodError || !isInputCompleted()}
            loading={isSubmitted}
          >
            申し込み
          </Button>
        </div>
        <div className={'pr-2'}>
          <Button color="secondary" onClick={onClose}>
            キャンセル
          </Button>
        </div>
      </div>
    </form>
  );
};
